import { Dispatch, FC, Fragment, SetStateAction, useEffect, useState } from 'react';
import { generalBlue, generalRed, googleMapConfig } from 'src/app-config';
import { generalHthHelper } from '@monorepo/helpers';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { houseToHouseServices } from 'src/services';
import { Hr, Loading, MapMarkerCurrentPosition } from 'src/components/_commons';
import { hthMapStyle, HTHMarkerBuilding } from 'src/components/_commons/house-to-house';
import { setAlertResponse } from 'src/store';
import { typeHTHBuilding, typeHTHFace, typeHTHTerritory } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    congregation: number;
    datetime: number;
    hthTerritories: typeHTHTerritory[];
    hthTerritory: typeHTHTerritory;
    serviceId: number;
    setHthTerritories: Dispatch<SetStateAction<typeHTHTerritory[] | null>>;
    usingLettersForBlocks: boolean;
};

export const HTHFeedbackTerritory: FC<propsType> = ({
    congregation, datetime, hthTerritories, hthTerritory, serviceId, setHthTerritories, usingLettersForBlocks
}) => {
    const [refreshCounter, setRefreshCounter] = useState(0);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();
    const mapLoader = useJsApiLoader(googleMapConfig);
    const month = new Date(datetime).getMonth();
    const year = new Date(datetime).getFullYear();

    const getBuildings = (polygon: typeHTHFace): typeHTHBuilding[] => {
        return polygon.buildings.filter(b => b.households.some(h => h.history.filter(x => !!x.state).map(x => x.timestamp).some(d => {
            const _date = new Date(d);
            return _date.getFullYear() === year && _date.getMonth() === month;
        })));
    }

    const updatePolygonIsCovered = (polygonId: number, isCovered: boolean) => {
        setHthTerritories(x =>
            x!.map(_t => _t.number === hthTerritory.number ?
                ({
                    ...hthTerritory,
                    faces: hthTerritory.faces.map(_p => _p.id === polygonId ? ({
                        ..._p,
                        color: isCovered ? generalRed : 'white',
                        isCovered
                        }) : _p)
                })
                :
                _t
            )
        );
        setRefreshCounter(x => x + 1);
    }

    useEffect(() => {
        if (!refreshCounter) {
            return;
        }
        houseToHouseServices.SetActivityFeedback(congregation, datetime, hthTerritories, serviceId).then(response => {
            if (!response.success) {
                dispatch(setAlertResponse({ response }));
                return;
            }
        });
    }, [refreshCounter]);

    // const saveHandler = async () => {
        // if (!hthTerritories) {
        //     return;
        // }
        // dispatch(setValuesAndOpenAlertModalReducer({
        //     title: 'Confirmar',
        //     message: `Se van a guardar los cambios para la salida del ${dateString}, ${territoryNumbers.length > 1 ? `territorios ${territoryNumbers.join(', ')}` : `territorio ${territoryNumbers.join('')}`}`,
        //     mode: 'confirm',
        //     execution: async () => {
        //         dispatch(showLoadingModalReducer());
        //         const success = await houseToHouseServices.SetActivityFeedback(congregation, datetime, hthTerritories);
        //         dispatch(hideLoadingModalReducer());
        //         if (!success) {
        //             dispatch(setValuesAndOpenAlertModalReducer({
        //                 title: 'Algo falló',
        //                 message: 'No se pudo guardar; ver si hay internet',
        //                 animation: 2,
        //                 mode: 'alert'
        //             }));
        //             return;
        //         }
        //         dispatch(setValuesAndOpenAlertModalReducer({
        //             title: 'Logrado',
        //             message: '¡Muchas gracias! Esta información es muy útil para la gestión del servicio del campo',
        //             animation: 1,
        //             mode: 'alert'
        //         }));
        //         navigate('/');
        //     }
        // }));
    // }

    return (
        <div className={'mt-5'}>
            <h1 className={`${isDarkMode ? 'bg-lightdark' : 'bg-grey'} fw-bolder text-center border-top border-bottom py-2`}>
                TERRITORIO {hthTerritory.territoryNumber}
            </h1>
            <div className={'mt-4'}>
                {mapLoader.isLoaded ?
                    <div className={'position-relative'} >
                        <GoogleMap
                            id={googleMapConfig.id}
                            mapContainerClassName={'d-block m-auto'}
                            mapContainerStyle={{
                                height: isMobile ? '600px' : '500px',
                                width: isMobile ? '100%' : '90%'
                            }}
                            options={{
                                center: hthTerritory.centerCoords,
                                disableDefaultUI: false,
                                draggable: isMobile,
                                fullscreenControl: false,
                                fullscreenControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER },
                                isFractionalZoomEnabled: true,
                                mapTypeControl: false,
                                minZoom: 8,
                                panControl: true,
                                streetViewControl: true,
                                styles: hthMapStyle,
                                zoom: hthTerritory.zoom,
                                zoomControl: true,
                                zoomControlOptions: { position: google.maps.ControlPosition.LEFT_BOTTOM }
                            }}
                        >
                            {!!hthTerritory.faces?.length && hthTerritory.faces.map(polygon =>
                                <Fragment key={polygon.id}>
                                    <Polygon
                                        onClick={() => updatePolygonIsCovered(polygon.id, !polygon.isCovered)}
                                        options={{
                                            clickable: true,
                                            fillColor: polygon.color ?? generalBlue,
                                            fillOpacity: 0.92,
                                            strokeColor: '',
                                            strokeOpacity: 0.9,
                                            strokePosition: google.maps.StrokePosition.CENTER,
                                            strokeWeight: 10
                                        }}
                                        path={[
                                            polygon.coordsPoint1,
                                            polygon.coordsPoint2,
                                            polygon.coordsPoint3
                                        ]}
                                    />
                                    {getBuildings(polygon).map((b, i) =>
                                        <HTHMarkerBuilding key={i}
                                            building={b}
                                            onClickHandler={() => {
                                                updatePolygonIsCovered(polygon.id, !polygon.isCovered);
                                            }}
                                        />
                                    )}
                                </Fragment>
                            )}
                            <MapMarkerCurrentPosition
                                showError={false}
                            />
                        </GoogleMap>
                    </div>
                    :
                    <Loading mt={'40px'} />
                }
            </div>
            <div className={`row ${isDarkMode ? '' : 'bg-light'} rounded pt-2 pb-3 mt-3 mb-4`}>
                <div className={`col-lg-2 ${isMobile ? 'ps-4' : ''}`}>
                    {isMobile &&
                        <Hr />
                    }
                    <div className={'d-flex align-items-center bg-grey text-dark pointer pt-2 ps-1 mt-1'}
                        style={{ paddingBottom: '13px' }}
                        onClick={() => {
                            setHthTerritories(x => x!.map(_t => _t.number === hthTerritory.number ? ({
                                ...hthTerritory,
                                faces: hthTerritory.faces.map(p => ({
                                    ...p,
                                    color: !hthTerritory.faces.every(p => p.isCovered) ? generalRed : 'white',
                                    isCovered: !hthTerritory.faces.every(p => p.isCovered)
                                }))
                            }) : _t));
                            setRefreshCounter(x => x + 1);
                        }}
                    >
                        <input type={'checkbox'}
                            className={'form-check-input checkbox-md checkbox-red ms-2 me-3'}
                            checked={hthTerritory.faces.every(p => p.isCovered)}
                            onChange={() => {}}
                        />
                        <span className={'fw-bold'}>
                            Todo el territorio
                        </span>
                    </div>
                </div>
                <div className={`col-lg-6 ${isMobile ? 'ps-4' : ''}`}>
                    {[...new Set(hthTerritory.faces.map(p => p.block))].map((b, i) =>
                        <div key={b}>
                            {(!!i || isMobile) && b === '1' &&
                                <Hr />
                            }
                            <div className={'d-flex align-items-center bg-grey text-dark pointer pt-1 pb-2 ps-1 mt-1'}
                                onClick={() => {
                                    setHthTerritories(x => x!.map(_t => _t.number === hthTerritory.number ? ({
                                        ...hthTerritory,
                                        faces: hthTerritory.faces.map(p => ({
                                            ...p,
                                            color: p.block === b ? !hthTerritory.faces.filter(p => p.block === b).every(p => p.isCovered) ? generalRed : 'white' : p.color,
                                            isCovered: p.block === b ? !hthTerritory.faces.filter(p => p.block === b).every(p => p.isCovered) : p.isCovered
                                        }))
                                    }) : _t));
                                    setRefreshCounter(x => x + 1);
                                }}
                            >
                                <input type={'checkbox'}
                                    className={`form-check-input checkbox-md checkbox-red ms-2 me-3 ${isMobile ? 'mb-0' : 'mb-2'}`}
                                    checked={hthTerritory.faces.filter(p => p.block === b).every(p => p.isCovered)}
                                    onChange={() => {}}
                                />
                                <div>
                                    <div className={'fw-bold'}>
                                        Toda la Manzana {generalHthHelper.MaskTheBlock(b, usingLettersForBlocks)}
                                    </div>
                                    <div className={'small'} style={{ fontSize: isMobile ? '0.7rem' : undefined }}>
                                        ({hthTerritory.faces.filter(p => p.block === b).map(p => p.street).join(', ')})
                                    </div>
                                </div>
                            </div>    
                        </div>    
                    )}
                </div>
                <div className={`col-lg-4 ${isMobile ? 'ps-4' : ''}`}>
                    {hthTerritory.faces.map((p, i) =>
                        <div key={p.id}>
                            {(!!i || isMobile) && p.face === 'A' &&
                                <Hr />
                            }
                            <div className={'d-flex align-items-center bg-grey text-dark pointer pt-1 pb-2 ps-1 mt-1'}
                                onClick={() => {
                                    setHthTerritories(x => x!.map(_t => _t.number === hthTerritory.number ? ({
                                        ...hthTerritory,
                                        faces: hthTerritory.faces.map(_p => ({
                                            ..._p,
                                            color: _p.id === p.id ? !p.isCovered ? generalRed : 'white' : _p.color,
                                            isCovered: _p.id === p.id ? !p.isCovered : _p.isCovered
                                        }))
                                    }) : _t));
                                    setRefreshCounter(x => x + 1);
                                }}
                            >
                                <input type={'checkbox'}
                                    className={'form-check-input checkbox-md checkbox-red ms-2 me-3'}
                                    checked={hthTerritory.faces.find(_p => _p.id === p.id)!.isCovered}
                                    onChange={() => {}}
                                />
                                <span className={'fw-bold'}>
                                    Manzana {generalHthHelper.MaskTheBlock(p.block, usingLettersForBlocks)} - Calle {p.street}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
